import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { ReportingDashboardControls } from 'teacher-ui-client/components/reporting/dashboard-controls'
import { ReportingDashboardDynamic } from 'teacher-ui-client/components/tiles/reporting-dashboard/dynamic'
import { DashboardTotalUsageOverTime } from 'teacher-ui-client/components/giraffe/charts/dashboard/total-usage-over-time'
import { t } from 'ember-intl'
import { ReportingDashboardStatic } from 'teacher-ui-client/components/tiles/reporting-dashboard/static'
import { DashboardCourseProgress } from 'teacher-ui-client/components/giraffe/charts/dashboard/course-progress'
import { DashboardLexileGrowth } from 'teacher-ui-client/components/giraffe/charts/dashboard/lexile-growth'
import { DashboardBooksReadByGenre } from 'teacher-ui-client/components/giraffe/charts/dashboard/books-read/by-genre'
import { DashboardAssessmentScores } from 'teacher-ui-client/components/giraffe/charts/dashboard/assessment-scores'
import { DashboardStoriesWritten } from 'teacher-ui-client/components/giraffe/charts/dashboard/stories-written'
import { AwardsEarnedRibbons } from 'teacher-ui-client/components/charts/dashboard/awards-earned/ribbons'
import { DashboardSkillsMasteredRe } from 'teacher-ui-client/components/charts/dashboard/skills-mastered/reading-eggs'
import type ReDashboardIndexRoute from 'teacher-ui-client/reporting/ui-scope/re/dashboard/index/route'
import Component from '@glimmer/component'
import type RouterService from '@ember/routing/router-service'
import { service } from '@ember/service'
import { themeKeys as statBoxThemes } from 'teacher-ui-client/components/giraffe/stat-box/themes'
import { isPresent } from '@ember/utils'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import { LinkTo } from '@ember/routing'
import { not } from 'ember-truth-helpers'
import { array } from '@ember/helper'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import RouteTemplate from 'ember-route-template'

interface Signature {
  Args: {
    model: ModelFor<ReDashboardIndexRoute>
  }
}
export class ReportingReDashboardIndexPage extends Component<Signature> {
  @service router!: RouterService

  readonly routePrefix = 'reporting.ui-scope.re.dashboard'

  get totalUsageBoxes() {
    return [
      {
        category: 'lessons',
        theme: statBoxThemes.THEME_OCEANY_BLUE,
      },
      {
        category: 'quizzes_reading',
        theme: statBoxThemes.THEME_FOREST_GREEN,
      },
      {
        category: 'assessments',
        theme: statBoxThemes.THEME_JUICY_ORANGE,
      },
      {
        category: 'driving_tests',
        theme: statBoxThemes.THEME_WL_BLUE,
      },
      {
        category: 'books',
        theme: statBoxThemes.THEME_PURPLY_PINK,
      },
      {
        category: 'spelling',
        theme: statBoxThemes.THEME_GRAPEY_GREEN,
      },
    ]
  }

  get isSummaryDataPresent() {
    return isPresent(this.args.model.totalUsageOverTime)
  }

  goToCategory = (category: PropertyKey) => {
    const paths: Record<PropertyKey, string> = {
      lessons: 'course-progress.reading',
      quizzes_reading: 'skills-mastered.cohort.students-results',
      driving_tests: 'driving-tests',
      assessments: 'assessment-scores',
      books: 'books-read',
      spelling: 'course-progress.spelling',
    }
    const path = paths[category]

    if (path) void this.router.transitionTo(`reporting.ui-scope.re.${path}`)
  }

  <template>
    <div class="mx-4">
      <ReportingTeacherUiHeader @title={{t "reporting.dashboardTitles.re"}} />
      <span class="float-right print:hidden">
        <PhosphorIcon @icon="phosphor-info-fill" class="text-dusty-black-500 h-3 w-3 cursor-pointer" />
        <Tooltip @text={{t "reporting.dashboardTooltip"}} />
      </span>
      <ReportingDashboardControls
        @students={{@model.scopedStudents}}
        @routePrefix={{this.routePrefix}}
        @exportOptions={{array ExportType.print}}
        @isDataPresent={{this.isSummaryDataPresent}}
        @studentRouteName="reporting.ui-scope.re.student-summary"
      />

      <ReportingDashboardDynamic
        data-test-total-usage-row
        class="page-break-after-always mb-4"
        @empty={{not this.isSummaryDataPresent}}
        @title={{t "reporting.chartTitles.dashboard.totalUsageOverTime"}}
        @tooltip={{t "reporting.tooltips.dashboard.totalUsageOverTime"}}
      >
        {{#if @model.totalUsageOverTime}}
          <DashboardTotalUsageOverTime
            @product={{@model.product}}
            @boxes={{this.totalUsageBoxes}}
            @data={{@model.totalUsageOverTime}}
            @tileClickAction={{this.goToCategory}}
            @uiScope={{@model.uiScope.scope}}
          />
        {{/if}}
      </ReportingDashboardDynamic>

      <div class="grid auto-rows-[26rem] grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 print:grid-cols-3">
        <LinkTo data-test-course-progress-reading @route="reporting.ui-scope.re.course-progress.reading">
          <ReportingDashboardStatic
            class="h-full"
            @empty={{not @model.courseProgressByGrade}}
            @title={{t "reporting.chartTitles.dashboard.courseProgressByGrade.re"}}
            @tooltip={{t "reporting.tooltips.dashboard.courseProgressByGrade.re"}}
          >
            {{#if @model.courseProgressByGrade}}
              <DashboardCourseProgress
                @data={{@model.courseProgressByGrade}}
                @product={{@model.product}}
                @precinct="reading"
              />
            {{/if}}
          </ReportingDashboardStatic>
        </LinkTo>

        <LinkTo data-test-lexile-details @route="reporting.ui-scope.re.lexile-details">
          <ReportingDashboardStatic
            class="h-full"
            @empty={{not @model.lexileGrowth}}
            @title={{t "reporting.chartTitles.dashboard.lexileGrowth"}}
            @tooltip={{t "reporting.tooltips.dashboard.lexileGrowth"}}
          >
            {{#if @model.lexileGrowth}}
              <DashboardLexileGrowth @data={{@model.lexileGrowth}} />
            {{/if}}
          </ReportingDashboardStatic>
        </LinkTo>

        <LinkTo data-test-assessments-completed @route="reporting.ui-scope.re.assessment-scores">
          <ReportingDashboardStatic
            class="h-full"
            @empty={{not @model.assessmentScores}}
            @title={{t "reporting.chartTitles.dashboard.assessmentScores.re"}}
            @tooltip={{t "reporting.tooltips.dashboard.assessmentScores.re"}}
          >
            {{#if @model.assessmentScores}}
              <DashboardAssessmentScores @data={{@model.assessmentScores}} />
            {{/if}}
          </ReportingDashboardStatic>
        </LinkTo>

        <LinkTo data-test-course-progress-spelling @route="reporting.ui-scope.re.course-progress.spelling">
          <ReportingDashboardStatic
            class="h-full"
            @empty={{not @model.spellingCourseProgress}}
            @title={{t "reporting.chartTitles.dashboard.spellingCourseProgress"}}
            @tooltip={{t "reporting.tooltips.dashboard.spellingCourseProgress"}}
          >
            {{#if @model.spellingCourseProgress}}
              <DashboardCourseProgress
                @data={{@model.spellingCourseProgress}}
                @product={{@model.product}}
                @precinct="spelling"
              />
            {{/if}}
          </ReportingDashboardStatic>
        </LinkTo>

        <LinkTo data-test-books-read-by-genre @route="reporting.ui-scope.re.books-read">
          <ReportingDashboardStatic
            class="h-full"
            @empty={{not @model.booksReadByGenre}}
            @title={{t "reporting.chartTitles.dashboard.booksReadByGenre.re"}}
            @tooltip={{t "reporting.tooltips.dashboard.booksReadByGenre"}}
          >
            {{#if @model.booksReadByGenre}}
              <DashboardBooksReadByGenre @data={{@model.booksReadByGenre}} />
            {{/if}}
          </ReportingDashboardStatic>
        </LinkTo>

        <LinkTo data-test-stories-written @route="reporting.ui-scope.re.stories-written">
          <ReportingDashboardStatic
            class="h-full"
            @empty={{not @model.storiesWritten}}
            @title={{t "reporting.chartTitles.dashboard.storiesWritten"}}
            @tooltip={{t "reporting.tooltips.dashboard.storiesWritten"}}
          >
            {{#if @model.storiesWritten}}
              <DashboardStoriesWritten @data={{@model.storiesWritten}} />
            {{/if}}
          </ReportingDashboardStatic>
        </LinkTo>

        <LinkTo data-test-awards-earned @route="reporting.ui-scope.re.awards-earned">
          <ReportingDashboardStatic
            class="h-full"
            @empty={{not @model.awardsEarned}}
            @title={{t "reporting.chartTitles.dashboard.awardsEarned.re"}}
            @tooltip={{t "reporting.tooltips.dashboard.awardsEarned.re" htmlSafe=true}}
          >
            {{#if @model.awardsEarned}}
              <AwardsEarnedRibbons class="h-full" @data={{@model.awardsEarned}} />
            {{/if}}
          </ReportingDashboardStatic>
        </LinkTo>

        <LinkTo data-test-skills-mastered @route="reporting.ui-scope.re.skills-mastered.cohort.students-results">
          <ReportingDashboardStatic
            class="h-full"
            @empty={{not @model.skillsMastered}}
            @title={{t "reporting.chartTitles.dashboard.skillsMastered"}}
            @tooltip={{t "reporting.tooltips.dashboard.skillsMastered.re"}}
          >
            {{#if @model.skillsMastered}}
              <DashboardSkillsMasteredRe @data={{@model.skillsMastered}} />
            {{/if}}
          </ReportingDashboardStatic>
        </LinkTo>
      </div>
    </div>
  </template>
}

export default RouteTemplate(ReportingReDashboardIndexPage)
